import {
  LIGHTING_TARGET,
  type DeviceManifest,
  type LightingEffectDefinition,
} from '@rapoo/device-sdk'

/**
 * 非 eager：只有 loadManifests() 时才请求各清单文件。
 * 避免与 @rapoo/device-sdk 在 connected→DeviceFactory 窗口并行抢请求/transform 队列。
 */
const manifestLoaders = import.meta.glob<DeviceManifest | DeviceManifest[]>(
  ['./*.ts', '!./index.ts', '!./lightingPresets.ts'],
  {
    import: 'default',
  },
)

const channel = import.meta.env.VITE_CHANNEL

let cachedManifests: DeviceManifest[] | null = null
let loadingPromise: Promise<DeviceManifest[]> | null = null

function filterManifests(list: DeviceManifest[]): DeviceManifest[] {
  return list.filter((m) => {
    if (!m || !m.model) return false
    if (!channel) return true
    const channels = m.channel
    if (!channels) return true
    return Array.isArray(channels) ? channels.includes(channel) : channels === channel
  })
}

/** 异步加载并缓存全部设备清单（可安全并发调用） */
export async function loadManifests(): Promise<DeviceManifest[]> {
  if (cachedManifests) return cachedManifests
  if (loadingPromise) return loadingPromise

  loadingPromise = (async () => {
    const modules = await Promise.all(Object.values(manifestLoaders).map((load) => load()))
    const flat = modules.flatMap((module) => (Array.isArray(module) ? module : [module]))
    cachedManifests = filterManifests(flat)
    console.log(`[manifests] loaded ${cachedManifests.length}/${modules.length}`)
    return cachedManifests
  })()

  try {
    return await loadingPromise
  } catch (error) {
    loadingPromise = null
    throw error
  }
}

export function getManifests(): DeviceManifest[] {
  return cachedManifests ?? []
}

export function findManifest(vid: number, pid: number): DeviceManifest | undefined {
  return getManifests().find((m) => {
    const pids = Array.isArray(m.hardware.productId) ? m.hardware.productId : [m.hardware.productId]
    return m.hardware.vendorId === vid && pids.includes(pid)
  })
}

export function findManifestByModel(model: string): DeviceManifest | undefined {
  return getManifests().find((m) => m.model === model)
}

export function findKeyboardLightEffect(
  model: string,
  effectId: number,
): LightingEffectDefinition | undefined {
  const manifest = findManifestByModel(model)
  const caps = manifest?.capabilities || []
  const effects = caps.reduce((acc: LightingEffectDefinition[], cap) => {
    if (cap.type === 'lighting' && cap.config.effects) {
      const effectArray = Array.isArray(cap.config.effects) ? cap.config.effects : []
      acc = [...acc, ...effectArray.filter((effect) => effect.target === LIGHTING_TARGET.Keyboard)]
    }
    return acc
  }, [])

  return effects.find((effect) => effect.id === effectId)
}
